import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { HEADER, NAV } from 'src/layouts/config-layout';
import { useNavPinned } from 'src/layouts/dashboard/nav-pinned-context';
import HorizontalPager from 'src/components/horizontal-pager';
import { CountryBarProps, CountryItem } from './types';
import { useGetBQCountriesQuery } from 'src/context/api';
import Iconify from 'src/components/iconify';


// Component to handle icon loading with loader
function IconifyWithLoader({ icon, width, height }: { icon: string; width: number; height: number }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const iconRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        // Reset loading state when icon changes
        setIsLoaded(false);

        // Check if icon is already loaded
        const checkIconLoaded = () => {
            if (iconRef.current) {
                const svg = iconRef.current.querySelector('svg');
                if (svg) {
                    setIsLoaded(true);
                }
            }
        };

        // Use MutationObserver to detect when the icon SVG is injected into the DOM
        if (iconRef.current) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        checkIconLoaded();
                    }
                });
            });

            observer.observe(iconRef.current, {
                childList: true,
                subtree: true,
            });

            // Also check immediately in case it's already loaded
            checkIconLoaded();

            // Fallback timeout to show icon after 2 seconds even if not detected
            const timeout = setTimeout(() => {
                setIsLoaded(true);
            }, 2000);

            return () => {
                observer.disconnect();
                clearTimeout(timeout);
            };
        }
    }, [icon]);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                width: width,
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {!isLoaded && (
                <CircularProgress
                    size={10}
                    thickness={5}
                    sx={{
                        color: '#919EAB',
                    }}
                />
            )}
            <Box
                ref={iconRef}
                sx={{
                    display: isLoaded ? 'inline-flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon={icon} width={width} height={height} />
            </Box>
        </Box>
    );
}

const ALL_OPTION: CountryItem = { country_code: '__all__', country_name: 'All' };

// This component is the canonical country selector (formerly `CountryFilter` in
// components/visibility/). The default code path is byte-for-byte the preprod
// CountryFilter so existing call sites (visibility, activations, brand-pulse)
// render identically. The `includeAll` mode is purely additive: only the funnel
// page passes it, and it prepends an "All" option that resolves to
// `onCountryChange(null)`.
export function CountryBar({
    countries,
    selectedCountry,
    onCountryChange,
    includeAll,
    enableDeepLinking = true,
    renderCountryLabel,
    stickyTop,
    loading,
    topGap = 15,
    contentPaddingX,
    selectedColor,
    selectedBorderWidth,
    reserveGapWhenHidden = false,
    tabSpacing,
}: CountryBarProps) {
    const theme = useTheme();
    const navPinned = useNavPinned();
    // Match the dashboard main content offset exactly (layouts/dashboard/main.tsx:
    // ml = navPinned ? W_VERTICAL_EXPANDED : W_VERTICAL). The previous `- 15` fudge
    // left the fixed bar 15px left of the page content whenever the nav was collapsed.
    const navWidth = navPinned ? NAV.W_VERTICAL_EXPANDED : NAV.W_VERTICAL;
    const { data: baseCountries }: any = useGetBQCountriesQuery(void 0);

    const items = includeAll ? [ALL_OPTION, ...countries] : countries;

    // Map region names to country codes
    const getCountryCodeFromRegionName = (regionName: string, countryList: any[]): string | null => {
        if (!countryList || countryList.length === 0) return null;

        // Try exact match first
        const exactMatch = countryList.find(
            (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
        );
        if (exactMatch) return exactMatch.country_code;

        // Try partial match (e.g., "France" matches "France")
        const partialMatch = countryList.find(
            (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
                c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
        );
        if (partialMatch) return partialMatch.country_code;

        // Fallback: try to extract code from region name if it looks like a code
        if (regionName && regionName.length === 2) {
            return regionName.toUpperCase();
        }

        return null;
    };

    const codeToName = useMemo(() => {
        const map = new Map<string, string>();
        countries.forEach((c: any) => {
            map.set(String(c.country_code).toUpperCase(), c.country_name);
        });
        return map;
    }, [countries]);

    const [didApplyDeepLink, setDidApplyDeepLink] = useState(false);

    useEffect(() => {
        if (didApplyDeepLink || !enableDeepLinking) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const countryName = codeToName.get(codeParam.toUpperCase());
                if (countryName && countryName !== selectedCountry) {
                    onCountryChange(countryName);
                }
            }
        } catch { }
        setDidApplyDeepLink(true);
    }, [codeToName, onCountryChange, didApplyDeepLink, selectedCountry, enableDeepLinking]);

    const loadingItems = Array.from({ length: 10 }, (_, index) => (
        <Stack key={index} direction="row" gap={0.75} width="10%">
            <Skeleton variant="rounded" width={"12.5%"} animation="wave" />
            <Skeleton variant="rectangular" width={"90%"} animation="wave" />
        </Stack>
    ))

    if (!loading && countries.length <= 1) {
        // Mono-country: the bar (and its in-flow spacer) would vanish entirely,
        // leaving the content below flush against the secondary nav. Opt-in callers
        // keep the spacer so their cards retain the same top offset as multi-country.
        return reserveGapWhenHidden ? <Box sx={{ height: topGap }} /> : null;
    }

    // Index of the entry to bring into view. With includeAll, a null selection
    // highlights the leading "All" entry; otherwise this matches preprod exactly.
    const activeIndex = includeAll
        ? (
            selectedCountry === 'ALL'
                ? 0
                : items.findIndex((c: any) => c.country_name === selectedCountry)
        )
        : (
            selectedCountry
                ? countries.findIndex((c: any) => c.country_name === selectedCountry)
                : 0
        );

    return (
        <>
            <Box sx={{ height: topGap }} />
            <Paper
                sx={{
                    p: 0,
                    px: contentPaddingX,
                    height: '42px',
                    width: '100%',
                    bgcolor: '#F7F7F7',
                    overflow: 'hidden',
                    borderBottom: '1px solid #EAEAEA',
                    position: 'fixed',
                    top: stickyTop ?? HEADER.H_DESKTOP * 2,
                    left: { lg: navWidth },
                    right: 0,
                    borderRadius: 0,
                    zIndex: theme.zIndex.appBar - 1,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {loading ?
                    (
                        // Default call sites keep px={5} (byte-identical to preprod). When the
                        // caller pads the bar itself (funnel passes contentPaddingX), the inner
                        // padding is dropped so the skeleton aligns with the country tabs.
                        <Box display="flex" flexDirection="row" px={contentPaddingX != null ? 0 : 5} alignItems="center" width="100%" gap={1.5}>
                            {loadingItems}
                        </Box>
                    ) : (
                        <HorizontalPager
                            items={items}
                            getItemKey={(c) => c.country_code}
                            activeIndex={activeIndex}
                            spacing={tabSpacing}
                            noArrowGutter={contentPaddingX != null}
                            renderItem={(c: CountryItem, index: number) => {
                                const isAll = c.country_code === '__all__';
                                const isSelected = isAll
                                    ? selectedCountry === 'ALL'
                                    : selectedCountry === c.country_name;
                                const countryCode = isAll
                                    ? null
                                    : getCountryCodeFromRegionName(c.country_name, baseCountries);

                                return (
                                    <Button
                                        key={c.country_code}
                                        variant="text"
                                        color="inherit"
                                        disableRipple
                                        disableFocusRipple
                                        disableTouchRipple
                                        onClick={() => {
                                            if (isAll) {
                                                onCountryChange('ALL');
                                                return;
                                            }
                                            if (selectedCountry === c.country_name) return;
                                            onCountryChange(c.country_name);
                                        }}
                                        sx={{
                                            // The leading tab drops its left padding (funnel/explore mode)
                                            // so its content sits flush with the page content / cards below.
                                            // With includeAll the leading item is "All" (index 0); without it,
                                            // the first country leads — both align identically via index === 0.
                                            pl: contentPaddingX != null && index === 0 ? 0 : '6px',
                                            pr: '6px',
                                            py: '14px',
                                            height: '42px',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                                boxShadow: 'none',
                                                opacity: .7,
                                            },
                                            width: 'fit-content',
                                            background: "none",
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            borderRadius: 0,
                                            borderBottom: isSelected
                                                ? `${selectedBorderWidth ?? 3}px solid ${selectedColor ?? 'black'}`
                                                : '2px solid transparent',
                                            boxSizing: 'border-box',
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        {!isAll && (
                                            <IconifyWithLoader icon={`circle-flags:${countryCode}`} width={16} height={16} />
                                        )}

                                        <Typography
                                            sx={{
                                                fontWeight: 400,
                                                fontSize: ".875rem",
                                            }}
                                            color={isSelected ? "black" : "#919EAB"}
                                        >
                                            {renderCountryLabel
                                                ? renderCountryLabel(c, isSelected)
                                                : c.country_name}
                                        </Typography>

                                    </Button>
                                );
                            }}
                        />
                    )}
            </Paper>
        </>
    );
}
